import type { ForwardRefRenderFunction } from 'react';

import Calendar from '@crehana/compass.icons.outline/Calendar';
import { clsx } from 'clsx';
import moment, { type Moment } from 'moment';
import DateInput from 'react-datetime';

import 'moment/locale/es';
import 'moment/locale/pt';

import { DatePickerWrapper } from './DatePickerTw.styles';
import type { DatePickerTwProps } from './types';

const DatePickerTw: ForwardRefRenderFunction<
  HTMLInputElement,
  DatePickerTwProps
> = ({
  className,
  css,
  disabled,
  errorText,
  hint,
  isDark = false,
  label,
  langCode,
  limits,
  name,
  onSelect,
  rtl,
  value,
  ...restProps
}) => {
  const validateLimits = (day: Moment) => {
    if (!limits) {
      // Preserved previous behaviour, but needs to be changed to return true
      return day.isAfter(moment().subtract(1, 'day'));
    }
    if (limits.minDate && limits.maxDate) {
      return day.isAfter(limits.minDate) && day.isBefore(limits.maxDate);
    }
    if (limits.minDate) {
      return day.isAfter(limits.minDate);
    }
    if (limits.maxDate) {
      return day.isBefore(limits.maxDate);
    }
    return true;
  };

  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: label association handled differently
    <label className={clsx(className, `tw-inline-flex tw-flex-col `)}>
      {label && (
        <span
          className={clsx(
            'tw-font-subtitle-5 tw-mb-0.8',
            isDark ? 'text-white' : 'text-base-main',
          )}
        >
          {label}
        </span>
      )}
      <DatePickerWrapper isDark={isDark} rtl={rtl} disabled={disabled}>
        <DateInput
          locale={langCode || 'es'}
          isValidDate={validateLimits}
          timeFormat={false}
          value={value || ''}
          className={clsx(
            isDark
              ? disabled
                ? 'tw-bg-neutral-dark-500'
                : 'tw-bg-neutral-dark-700'
              : disabled
                ? 'tw-bg-neutral-light-400'
                : 'tw-bg-white',
            isDark
              ? errorText
                ? 'tw-border-alert-300'
                : 'tw-border-transparent'
              : errorText
                ? 'tw-border-alert-500'
                : 'tw-border-neutral-light-500',
            disabled ? 'tw-border-none' : 'tw-border',
            `tw-rounded-1 tw-border-solid`,
          )}
          onChange={val =>
            onSelect && onSelect(moment(val).format('DD/MM/YYYY'))
          }
          renderInput={(props, openCalendar) => (
            <div className={clsx('tw-flex tw-w-full tw-items-end')}>
              <input
                {...props}
                {...restProps}
                value={value || ''}
                onClick={() => openCalendar()}
                name={name}
                disabled={disabled}
                autoComplete="off"
                readOnly
                className={clsx(
                  isDark
                    ? disabled
                      ? 'tw-text-neutral-dark-600'
                      : 'tw-text-neutral-light-100'
                    : 'text-body',
                  'tw-w-full tw-h-4 tw-z-1 tw-border-0 tw-bg-transparent tw-font-body4 tw-pl-1.6 tw-pr-0.8 tw-resize-none focus:tw-outline-none',
                )}
                placeholder={hint}
                css={css}
              />
              <div className="tw-flex tw-items-center tw-justify-center tw-pr-1.6 tw-h-4">
                <Calendar
                  className={clsx(
                    isDark
                      ? disabled
                        ? 'tw-text-neutral-dark-600'
                        : 'tw-text-neutral-dark-500'
                      : disabled
                        ? 'tw-text-neutral-light-600'
                        : 'tw-text-neutral-light-700',
                  )}
                  size={24}
                />
              </div>
            </div>
          )}
        />
      </DatePickerWrapper>
      {errorText && (
        <span
          className={clsx(
            isDark ? ' tw-text-alert-300' : 'tw-text-alert-500',
            'tw-font-caption tw-mt-0.8',
          )}
          role="alert"
        >
          {errorText}
        </span>
      )}
    </label>
  );
};

export default DatePickerTw;
